// Single source of truth for fonts used across the editor and book cover designer.
// - name:  display label and the primary font name used by Fabric.js / canvas
// - stack: full CSS font-family stack for cross-platform rendering in the editor
//
// All Google Fonts in this list are loaded globally in public/index.html. The
// typefaces the manuscript Theme Editor offers (src/config/manuscriptThemes.js)
// are here too, so a font a theme names is one the editor can show and pick.
// System fonts (Arial, Georgia, etc.) need no external load.

export const FONT_FAMILIES = [
  { name: 'Alegreya',           stack: 'Alegreya, Georgia, serif' },
  { name: 'Arial',              stack: 'Arial, Helvetica, sans-serif' },
  { name: 'Baloo 2',            stack: '"Baloo 2", "Helvetica Neue", Arial, sans-serif' },
  { name: 'Baskerville',        stack: 'Baskerville, "Baskerville Old Face", "Palatino Linotype", serif' },
  { name: 'Bodoni Moda',        stack: '"Bodoni Moda", Didot, Georgia, serif' },
  { name: 'Cinzel',             stack: 'Cinzel, "Trajan Pro", Georgia, serif' },
  { name: 'Cormorant Garamond', stack: '"Cormorant Garamond", Garamond, Georgia, serif' },
  { name: 'Courier New',        stack: '"Courier New", Courier, "Andale Mono", monospace' },
  { name: 'Courier Prime',      stack: '"Courier Prime", "Courier New", Courier, monospace' },
  { name: 'Crimson Pro',        stack: '"Crimson Pro", Georgia, serif' },
  { name: 'Dancing Script',     stack: '"Dancing Script", "Brush Script MT", cursive' },
  { name: 'EB Garamond',        stack: '"EB Garamond", Garamond, "Palatino Linotype", serif' },
  { name: 'Georgia',            stack: 'Georgia, "Times New Roman", serif' },
  { name: 'Helvetica Neue',     stack: '"Helvetica Neue", Helvetica, Arial, sans-serif' },
  { name: 'Impact',             stack: 'Impact, Haettenschweiler, "Arial Narrow Bold", sans-serif' },
  { name: 'Inter',              stack: 'Inter, "Helvetica Neue", Arial, sans-serif' },
  { name: 'Josefin Sans',       stack: '"Josefin Sans", "Helvetica Neue", Arial, sans-serif' },
  { name: 'Lato',               stack: 'Lato, "Helvetica Neue", Arial, sans-serif' },
  { name: 'Libre Baskerville',  stack: '"Libre Baskerville", Baskerville, Georgia, serif' },
  { name: 'Libre Caslon Text',  stack: '"Libre Caslon Text", Georgia, serif' },
  { name: 'Literata',           stack: 'Literata, Georgia, serif' },
  { name: 'Lobster',            stack: 'Lobster, cursive' },
  { name: 'Lora',               stack: 'Lora, Georgia, serif' },
  { name: 'Merriweather',       stack: 'Merriweather, Georgia, serif' },
  { name: 'Montserrat',         stack: 'Montserrat, "Helvetica Neue", Arial, sans-serif' },
  { name: 'Nunito',             stack: 'Nunito, "Helvetica Neue", Arial, sans-serif' },
  { name: 'Open Sans',          stack: '"Open Sans", "Helvetica Neue", Arial, sans-serif' },
  { name: 'Oswald',             stack: 'Oswald, "Helvetica Neue", Arial, sans-serif' },
  { name: 'Pacifico',           stack: 'Pacifico, cursive' },
  { name: 'Palatino',           stack: '"Palatino Linotype", Palatino, "Book Antiqua", serif' },
  { name: 'Playfair Display',   stack: '"Playfair Display", "Palatino Linotype", Georgia, serif' },
  { name: 'Roboto',             stack: 'Roboto, "Helvetica Neue", Arial, sans-serif' },
  { name: 'Source Code Pro',    stack: '"Source Code Pro", "Courier New", Courier, monospace' },
  { name: 'Source Serif 4',     stack: '"Source Serif 4", Georgia, "Times New Roman", serif' },
  { name: 'Spectral',           stack: 'Spectral, Georgia, serif' },
  { name: 'Times New Roman',    stack: '"Times New Roman", Times, serif' },
  { name: 'Trebuchet MS',       stack: '"Trebuchet MS", "Helvetica Neue", Arial, sans-serif' },
  { name: 'Verdana',            stack: 'Verdana, Geneva, "DejaVu Sans", sans-serif' },
  { name: 'Work Sans',          stack: '"Work Sans", "Helvetica Neue", Arial, sans-serif' },
];

// Convenience: bare name list for Fabric.js / canvas consumers
export const FONT_NAMES = FONT_FAMILIES.map(f => f.name);

// Sizes and named text styles, shared by every toolbar that offers them — the
// editor's, and the Manuscript Structure page's. They lived as private constants
// in EditorToolbar.jsx; they are here so a second toolbar cannot drift from the
// first, which is the same reason FONT_FAMILIES is here.
export const FONT_SIZES = [
  '8px', '9px', '10px', '11px', '12px', '14px', '16px', '18px',
  '20px', '22px', '24px', '26px', '28px', '32px', '36px', '40px',
  '44px', '48px', '54px', '60px', '66px', '72px', '80px', '88px', '96px',
];

// A named style is a preset, not a fourth independent control: picking one writes
// size, font, alignment and weight at once, and the individual controls then
// adjust from there.
export const TEXT_STYLES = [
  { value: 'normal',   label: 'Normal Text', size: '14px', font: 'Arial',   align: 'left',   bold: false, italic: false },
  { value: 'title',    label: 'Title',        size: '27px', font: 'Arial',   align: 'center', bold: true,  italic: false },
  { value: 'subtitle', label: 'Subtitle',     size: '18px', font: 'Georgia', align: 'center', bold: false, italic: true  },
  { value: 'heading1', label: 'Heading 1',    size: '24px', font: 'Arial',   align: 'left',   bold: true,  italic: false },
  { value: 'heading2', label: 'Heading 2',    size: '20px', font: 'Arial',   align: 'left',   bold: true,  italic: false },
  { value: 'heading3', label: 'Heading 3',    size: '18px', font: 'Arial',   align: 'left',   bold: true,  italic: false },
  { value: 'heading4', label: 'Heading 4',    size: '16px', font: 'Arial',   align: 'left',   bold: true,  italic: false },
  { value: 'quote',    label: 'Quote',        size: '14px', font: 'Georgia', align: 'left',   bold: false, italic: true  },
];

// The fallback is named rather than taken from the top of the list: the list is
// alphabetical, and Alegreya arriving at the top would have changed it.
const FALLBACK_FAMILY = FONT_FAMILIES.find((f) => f.name === 'Arial');

/** The CSS stack for a font name, falling back to Arial. */
export function fontStackFor(name) {
  const entry = FONT_FAMILIES.find((f) => f.name === name);
  return entry ? entry.stack : FALLBACK_FAMILY.stack;
}
